/* ═════════════════════════════════════════════════════════════════════════
   CART CHECKOUT BUYER-IDENTITY SYNC — v3 (Defense in Depth)
   ─────────────────────────────────────────────────────────────────────────
   Mike + Renee + Chris  —  2026-05-20 v1+v2, 2026-05-21 v3

   THE INVARIANT THIS SCRIPT GUARANTEES
     At every moment a customer could be about to check out, the cart's
     `buyerIdentity.preferences.delivery.deliveryMethod` MUST match the
     cart attribute "Order Type":

         Order Type = "Delivery"  →  preference = [SHIPPING] + address
         Order Type = "Pickup"    →  preference = [PICK_UP]
         Order Type = anything else (or missing)  →  preference cleared/untouched

     If this invariant holds:
       - Shopify checkout defaults to the correct tab (Ship for Delivery,
         Pickup for Pickup)
       - Layer 3 cart-validation Function never rejects /cart/add.js with
         the "You selected X..." 422 because there's no mismatch
       - Address pre-fills on the Ship tab for Delivery customers

   WHY V1 + V2 WEREN'T ENOUGH
     v1 (2026-05-20 morning): only set preference=SHIPPING for Delivery
     carts. Pickup carts left whatever preference was lying around. Caused
     wizard /cart/add.js to 422 if you came back to do a Pickup order
     after an earlier Delivery session.

     v2 (2026-05-20 evening): symmetric — set [SHIPPING] for Delivery,
     [PICK_UP] for Pickup. Listened for the WIZARD'S
     `order-scheduler:type-changed` event. But: the wizard is only ONE of
     several order-entry surfaces. Cake product pages, the daily cupcake
     menu, the cart-inline date/time picker all change Order Type via
     `OrderScheduler.setType()` which dispatches a DIFFERENT event
     (`order-scheduler:change`) that v2 didn't listen for. So Delivery on
     a cake landed customers on the Pickup tab at checkout, and a 4-pack
     box could leave a stale preference unsynced.

   V3 — DEFENSE IN DEPTH
     Four overlapping layers, any one alone is sufficient, all four
     together leave zero gaps:

     Layer A — Listen to EVERY OrderScheduler / cart event:
       `order-scheduler:change`     ← UNIVERSAL — fires from setType,
                                       setDate, setTime, clearAll on EVERY
                                       UI surface
       `order-scheduler:confirmed`  ← popup confirm button
       `order-scheduler:type-changed` ← wizard step 1 (kept for back-compat)
       `cart:refresh`               ← post-add-to-cart / cart drawer events
       `cart:open`                  ← cart drawer opening

     Layer B — Monkey-patch fetch:
       Wrap window.fetch. After any successful /cart/update.js or
       /cart/add.js response, re-sync buyer identity. Catches any code
       path that bypasses OrderScheduler entirely (third-party apps,
       direct fetch calls, future code).

     Layer C — Paranoid Checkout-button sync:
       On every Checkout button submit, ALWAYS re-sync buyer identity
       before letting the form submit. No cache, no shortcuts. The
       moment of truth — guarantees correct state regardless of what
       came before. Adds ~150-300ms to checkout click latency.

     Layer D — Page-load reconciliation:
       On every page load, fetch cart + read current buyer-identity
       preference. If preference doesn't match Order Type, fix it.
       Catches cross-tab / cross-session staleness (customer opens a
       new tab with an old cart, etc.).

   COMPATIBILITY
     - Standard Shopify plan (Storefront API works on all plans)
     - All product/cart/checkout entry points
     - Cart phone gate (we listen on `submit`, not `click`)
     - Express checkout buttons bypass our intercept (correct — they
       use their own delivery selection)
     - Wizard, product-page popup, cart-inline picker, daily menu

   FAILURE MODES
     - Storefront API down/slow → checkout click still fires, customer
       may land on Pickup tab. Layer 3 validator banner catches them.
     - Cart token format unexpected → silent fail.
     - Zip not in lookup → preference set without city pre-fill.

   ROLLBACK
     Delete the script tag in layout/theme.liquid + this file.
   ═════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ─── CONFIG ─────────────────────────────────────────────────────── */

  var STOREFRONT_TOKEN = '66dcbfa697e3435dd29d22419f021f48';
  var API_VERSION = '2025-10';

  /* Zip → city/state lookup for the Cupcake Co. delivery zone. Used only
     for Delivery preference (address pre-fill). Pickup ignores. */
  var ZIP_LOOKUP = {
    '22801': { city: 'Harrisonburg',   province: 'Virginia' },
    '22802': { city: 'Harrisonburg',   province: 'Virginia' },
    '22807': { city: 'Harrisonburg',   province: 'Virginia' },
    '22812': { city: 'Bridgewater',    province: 'Virginia' },
    '22815': { city: 'Broadway',       province: 'Virginia' },
    '22820': { city: 'Criders',        province: 'Virginia' },
    '22821': { city: 'Dayton',         province: 'Virginia' },
    '22824': { city: 'Edinburg',       province: 'Virginia' },
    '22827': { city: 'Elkton',         province: 'Virginia' },
    '22830': { city: 'Fulks Run',      province: 'Virginia' },
    '22831': { city: 'Hinton',         province: 'Virginia' },
    '22832': { city: 'Keezletown',     province: 'Virginia' },
    '22834': { city: 'Lacey Spring',   province: 'Virginia' },
    '22840': { city: 'Linville',       province: 'Virginia' },
    '22841': { city: 'Massanutten',    province: 'Virginia' },
    '22842': { city: 'Mount Crawford', province: 'Virginia' },
    '22845': { city: 'Mount Solon',    province: 'Virginia' },
    '22846': { city: 'New Market',     province: 'Virginia' },
    '22847': { city: 'Penn Laird',     province: 'Virginia' },
    '22848': { city: 'Pleasant Valley',province: 'Virginia' },
    '22849': { city: 'Quicksburg',     province: 'Virginia' },
    '22850': { city: 'Singers Glen',   province: 'Virginia' },
    '22851': { city: 'Timberville',    province: 'Virginia' },
    '22853': { city: 'Mount Crawford', province: 'Virginia' }
  };

  /* ─── STATE ──────────────────────────────────────────────────────── */

  /* Tracks the (cart token, orderType, addr zip) tuple we last synced.
     Used by Layers A, B, D for de-duplication. Layer C ignores this
     and always re-syncs (paranoid). */
  var lastSyncedKey = null;
  var inFlight = null;

  /* ─── HELPERS ────────────────────────────────────────────────────── */

  function getCart() {
    /* Always fetch fresh — Order Type can change mid-session from any
       order-entry surface. Stale cache caused v1/v2 bugs. */
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function readOrderType(cart) {
    if (!cart || !cart.attributes) return '';
    return String(cart.attributes['Order Type'] || '').trim().toLowerCase();
  }

  function readZip(cart) {
    if (!cart || !cart.attributes) return '';
    return String(cart.attributes['Delivery Zip'] || '').trim();
  }

  /* AUTHORITATIVE Order Type — prefers OrderScheduler sessionStorage over
     cart attribute. The inline product-page picker (cake, jar cakes, daily
     menu) calls `OS.setType()` which writes to sessionStorage IMMEDIATELY,
     but the cart attribute is only written on popup-confirm or wizard-step,
     not on inline-picker toggle. Without this, my script would read a stale
     cart attribute and set the wrong buyer-identity preference.
     v3.3 fix — 2026-05-21. */
  function readAuthOrderType(cart) {
    if (window.OrderScheduler && typeof window.OrderScheduler.getType === 'function') {
      var t = String(window.OrderScheduler.getType() || '').trim().toLowerCase();
      if (t === 'pickup' || t === 'delivery') return t;
    }
    return readOrderType(cart);
  }

  /* AUTHORITATIVE zip — same idea. Wizard writes 'cc_delivery_zip' to
     sessionStorage on its zip-check step. Inline pickers may not. */
  function readAuthZip(cart) {
    try {
      var z = String(sessionStorage.getItem('cc_delivery_zip') || '').trim();
      if (z) return z;
    } catch (e) { /* sessionStorage blocked — fall through */ }
    return readZip(cart);
  }

  /* If the cart attribute "Order Type" / "Pickup/Delivery Time" /
     "Pickup/Delivery Date" don't match the authoritative values (from
     OrderScheduler sessionStorage), push /cart/update.js to bring them
     into sync. v3.4 — Time + Date added 2026-05-21.

     Why all three together: when the inline picker on a PDP toggles
     Pickup ↔ Delivery, date-filter.js calls OS.clearTime() to wipe a
     stale slot. SessionStorage time becomes empty, but cart attribute
     time stays stale. Without syncing time too, the validator (which
     reads cart attribute time) sees the OLD time and may pass or fail
     wrongly. */
  function readOSValue(method) {
    if (window.OrderScheduler && typeof window.OrderScheduler[method] === 'function') {
      return String(window.OrderScheduler[method]() || '').trim();
    }
    return '';
  }

  function ensureCartAttrMatchesAuth(cart) {
    var authType = readAuthOrderType(cart);
    if (authType !== 'pickup' && authType !== 'delivery') {
      return Promise.resolve(cart);
    }

    var authTime = readOSValue('getTime');
    var authDate = readOSValue('getDate');
    var cartType = readOrderType(cart);
    var cartTime = String(cart.attributes['Pickup/Delivery Time'] || '').trim();
    var cartDate = String(cart.attributes['Pickup/Delivery Date'] || '').trim();

    var typeMatches = cartType === authType;
    var timeMatches = cartTime === authTime;
    var dateMatches = !authDate || cartDate === authDate; /* only require if OS has one */

    if (typeMatches && timeMatches && dateMatches) {
      return Promise.resolve(cart);
    }

    var displayType = authType === 'pickup' ? 'Pickup' : 'Delivery';
    var attrs = { 'Order Type': displayType };
    /* Push time even when authTime is "" (empty) — that explicitly
       CLEARS a stale cart-attribute time. */
    if (!timeMatches) attrs['Pickup/Delivery Time'] = authTime;
    if (!dateMatches) attrs['Pickup/Delivery Date'] = authDate;

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
    .then(function () { return getCart(); })
    .catch(function () { return cart; });
  }

  /* Returns the buyerIdentity payload to send, or null if we shouldn't
     touch the cart. Uses AUTHORITATIVE (sessionStorage-preferred) order
     type so the inline picker on cake / jar / daily-menu PDPs writes
     the correct preference. */
  function buildBuyerIdentity(cart) {
    var ot = readAuthOrderType(cart);

    if (ot === 'delivery') {
      var payload = {
        countryCode: 'US',
        preferences: { delivery: { deliveryMethod: ['SHIPPING'] } }
      };
      var zip = readAuthZip(cart);
      var loc = ZIP_LOOKUP[zip];
      if (loc) {
        payload.deliveryAddressPreferences = [{
          deliveryAddress: {
            country: 'US',
            province: loc.province,
            city: loc.city,
            zip: zip
          }
        }];
      }
      return payload;
    }

    if (ot === 'pickup') {
      return {
        countryCode: 'US',
        preferences: { delivery: { deliveryMethod: ['PICK_UP'] } }
      };
    }

    return null;
  }

  function callApi(cart, payload) {
    var query =
      'mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {' +
      '  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {' +
      '    cart { id }' +
      '    userErrors { field message code }' +
      '  }' +
      '}';

    return fetch('/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        query: query,
        variables: {
          cartId: 'gid://shopify/Cart/' + cart.token,
          buyerIdentity: payload
        }
      })
    })
    .then(function (r) { return r.json(); })
    .then(function (j) {
      return j && j.data && j.data.cartBuyerIdentityUpdate
          && (!j.data.cartBuyerIdentityUpdate.userErrors
              || j.data.cartBuyerIdentityUpdate.userErrors.length === 0);
    })
    .catch(function () { return false; });
  }

  /* Cache key: cart token + AUTH order type + zip + time.
     Including time means changing time slots (which fires
     order-scheduler:change via OS.setTime / OS.clearTime) invalidates
     the cache — so the time gets re-synced to cart attributes. */
  function makeKey(cart) {
    if (!cart || !cart.token) return null;
    return cart.token + '|' + readAuthOrderType(cart) + '|' + readAuthZip(cart) + '|' + readOSValue('getTime');
  }

  /* Full sync sequence (v3.3): pull cart → align cart attribute "Order
     Type" with sessionStorage → set buyer-identity preference. This
     ensures BOTH:
       1. Layer 3 validator + Layer 1 lock see the right cart attribute
       2. Shopify checkout sees the right buyer-identity preference */
  function fullSync(cart) {
    return ensureCartAttrMatchesAuth(cart).then(function (refreshedCart) {
      var payload = buildBuyerIdentity(refreshedCart);
      if (!payload) return false;
      return callApi(refreshedCart, payload).then(function (ok) {
        if (ok) lastSyncedKey = makeKey(refreshedCart);
        return ok;
      });
    });
  }

  /* Cached sync — used by Layers A, B, D. Skips the API call if we
     already synced for this exact (token, orderType, zip) tuple. */
  function syncBuyerIdentity() {
    if (inFlight) return inFlight;
    inFlight = getCart().then(function (cart) {
      if (!cart || !cart.token) return false;
      var key = makeKey(cart);
      if (lastSyncedKey === key) return true;
      return fullSync(cart);
    }).then(function (r) { inFlight = null; return r; });
    return inFlight;
  }

  /* Uncached sync — used by Layer C (Checkout button click). Always
     re-syncs even if the cache says we already did. */
  function forceSyncBuyerIdentity() {
    return getCart().then(function (cart) {
      if (!cart || !cart.token) return false;
      return fullSync(cart);
    });
  }

  /* ─── STALE-DATE GUARD (cross-day cart fix — 2026-06-04) ──────────────
     The cart is a ~14-day cookie; the date/type/time selection lives in
     sessionStorage and is GONE on a new session. So a customer can build a
     cart one day, return the next, and check out against a pickup date that
     has since passed (or fallen inside the lead-time window) — and our daily
     flavor menu may no longer carry what's in their box. The server-side
     Validation Function catches MIXED-day carts; this catches a SINGLE stale
     date by stopping checkout so they re-pick (and re-check flavors).

     Pairs with the "_Built For Date" line stamp + CHECK 0 in the cart
     Validation Function. Strictly FAIL-OPEN: every uncertain path returns
     `false` (not stale) so a valid checkout is never trapped. */

  /* Parse a pickup-date label to a Date WITHOUT relying on Date string
     parsing (which differs across the two formats the storefront emits:
     "Wednesday, June 3, 2026" from the wizard, "03 June 2026" from the PDP /
     popup pickers). Token-scan for month NAME + 4-digit year + 1–2 digit day,
     then build the Date explicitly. Returns null when it can't parse. */
  function parsePickupDate(str) {
    if (!str) return null;
    var MONTHS = {
      january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
      july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
    };
    var tokens = String(str).toLowerCase().replace(/,/g, ' ').split(/\s+/);
    var month = -1, year = 0, day = 0;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (!t) continue;
      if (MONTHS.hasOwnProperty(t)) { month = MONTHS[t]; }
      else if (/^\d{4}$/.test(t)) { year = parseInt(t, 10); }
      else if (/^\d{1,2}$/.test(t) && !day) { day = parseInt(t, 10); }
    }
    if (month < 0 || !year || !day) return null;
    var d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  /* Resolve to { dateLabel } when the cart's saved pickup/delivery date is no
     longer orderable, or false otherwise (incl. every can't-tell case). */
  function checkStaleDate() {
    return getCart().then(function (cart) {
      try {
        var OS = window.OrderScheduler;
        if (!OS || typeof OS.getMinDate !== 'function') return false; // can't judge
        if (!cart || !cart.attributes) return false;
        var raw = String(cart.attributes['Pickup/Delivery Date'] || '').trim();
        if (!raw) return false;                 // no saved date → allow
        var picked = parsePickupDate(raw);
        if (!picked) return false;              // unparseable → allow
        var min = OS.getMinDate();
        if (!min || isNaN(min.getTime())) return false;
        var p = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate()).getTime();
        var m = new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime();
        if (p < m) return { dateLabel: raw };   // saved date earlier than earliest orderable → stale
        return false;
      } catch (e) { return false; }             // any error → allow (never trap)
    }).catch(function () { return false; });
  }

  /* ─── LAYER C — PARANOID CHECKOUT-BUTTON SYNC ────────────────────── */

  function attachToForms() {
    var btns = document.querySelectorAll('button[name="checkout"]');
    btns.forEach(function (btn) {
      var form = btn.form;
      if (!form || form._ccBuyerIdAttached) return;
      form._ccBuyerIdAttached = true;

      form.addEventListener('submit', function (e) {
        /* Always re-sync at the moment of truth. No cache shortcuts. */
        e.preventDefault();
        e.stopPropagation();

        function proceed() {
          forceSyncBuyerIdentity().then(function () {
            form._ccBuyerIdAttached = false;
            HTMLFormElement.prototype.submit.call(form);
          });
        }

        /* Stale-date guard (cross-day fix): stop checkout if the saved
           pickup/delivery date is no longer orderable, so the customer
           re-picks (and re-checks flavors — the daily menu changes by the
           day). Fail-open: any uncertainty falls through to normal checkout. */
        checkStaleDate().then(function (stale) {
          if (!stale) { proceed(); return; }
          try {
            window.alert(
              'Your cart was started earlier, and the pickup/delivery date you ' +
              'chose (' + stale.dateLabel + ') is no longer available.\n\n' +
              'Please choose a new date — and double-check your flavors, since ' +
              'our daily menu changes by the day.'
            );
          } catch (e2) {}
          /* Re-arm so a later, valid submit still works, then send them back
             to the cart to re-pick. */
          form._ccBuyerIdAttached = false;
          try { window.location.href = '/cart'; } catch (e3) {}
        }).catch(function () { proceed(); });
      });
    });
  }

  /* ─── LAYER B — MONKEY-PATCH FETCH ───────────────────────────────── */

  function installFetchHook() {
    if (window._ccFetchHookInstalled) return;
    window._ccFetchHookInstalled = true;

    var orig = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartWrite = /\/cart\/(update|add|change|clear)\.js/.test(url);

      var p = orig.apply(this, arguments);
      if (!isCartWrite) return p;

      /* Don't intercept the response — just kick off a re-sync after it
         resolves. Use a small delay so Shopify has fully committed the
         cart state on its side. */
      return p.then(function (res) {
        try {
          if (res && (res.status === 200 || res.status === 201)) {
            setTimeout(function () {
              /* Force the cache to miss so we re-evaluate. */
              lastSyncedKey = null;
              syncBuyerIdentity();
            }, 150);
          }
        } catch (e) { /* never break the original fetch */ }
        return res;
      });
    };
  }

  /* ─── LAYER A — EVENT LISTENERS ──────────────────────────────────── */

  function installEventListeners() {
    /* Universal: every OrderScheduler.setType/setDate/setTime/clearAll
       fires this. Covers wizard, product-page popup, cart-inline picker,
       and any other UI using window.OrderScheduler. */
    document.addEventListener('order-scheduler:change', function () {
      /* Give the UI a tick to also push its /cart/update.js call (the
         scheduler dispatches the event sync but the cart write is
         async). */
      setTimeout(function () {
        lastSyncedKey = null;
        syncBuyerIdentity();
      }, 250);
    });

    /* Popup "Confirm" button — fires after the user finishes the
       product-page date/time/type selection. Belt for the suspenders. */
    document.addEventListener('order-scheduler:confirmed', function () {
      setTimeout(function () {
        lastSyncedKey = null;
        syncBuyerIdentity();
      }, 250);
    });

    /* Wizard step 1 specific event — kept for back-compat, even though
       order-scheduler:change above already catches this. */
    document.addEventListener('order-scheduler:type-changed', function () {
      setTimeout(function () {
        lastSyncedKey = null;
        syncBuyerIdentity();
      }, 250);
    });

    /* Cart drawer + cart-refresh events. */
    document.addEventListener('cart:open', function () {
      attachToForms();
    });
    document.addEventListener('cart:refresh', function () {
      lastSyncedKey = null;
      syncBuyerIdentity();
      attachToForms();
    });
  }

  /* ─── LAYER D — PAGE-LOAD RECONCILIATION ─────────────────────────── */

  function pageLoadReconcile() {
    /* Read cart attributes + current preference. If they disagree,
       fix. Done as part of normal syncBuyerIdentity since it always
       reads current state from /cart.js. */
    syncBuyerIdentity();
  }

  /* ─── BOOT ───────────────────────────────────────────────────────── */

  function boot() {
    /* Each layer wrapped in try/catch so a failure in any one doesn't
       cascade and break the others. */
    try { installEventListeners(); } catch (e) {}
    try { installFetchHook();      } catch (e) {}
    try { pageLoadReconcile();     } catch (e) {}
    try { attachToForms();         } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Watch for drawer cart injection (Shopify themes inject the drawer
     form dynamically). */
  var mo = new MutationObserver(attachToForms);
  mo.observe(document.body, { childList: true, subtree: true });

})();
